import objectKeysToCamelCase from '@grebban/utils/object/keysToCamelCase';
import { createAsyncThunk } from '@reduxjs/toolkit';
import { camelCaseMapValues } from '@fogia/core-web/utils/camelCaseMapValues';
import { inServer } from '@fogia/core-web/constants';
import Events from '@fogia/core-web/libs/Events';
import { PageEvents, ProductEvents } from '@fogia/core-web/libs/Events/constants';
import { ResolveSlug } from '@fogia/core-web/libs/GrebbCommerceAPI/Content';
import { formatLocaleAlternates } from '@fogia/core-web/state/page/helpers';
import getQueryParams from '@fogia/core-web/utils/getQueryParams';
import { GetStoreAvailabilityByPartNos } from '@fogia/core-web/libs/GrebbCommerceAPI/Products';

const RESOLVED_SLUG_TYPES = new Set(['contentPage', 'product', 'categoryPage', 'searchPage', 'redirect']);

export default createAsyncThunk('page/resolvePage', async (data, { getState, fulfillWithValue, rejectWithValue }) => {
    // @TODO do we need mutexLock here?
    const { applicationId } = getState().application.site;
    const { slug, location } = data;

    const fulfillNotFoundPage = () => {
        const { config, references } = getState().application;
        const notFoundPage = config?.notFoundPage ?? {};

        return fulfillWithValue({
            data: {
                ...notFoundPage,
                _references: references ?? {},
            },
            applicationId: applicationId || null,
            localeAlternates: [],
            relationships: null,
            // Kept null so no canonical is emitted for a URL that doesn't exist.
            resolvedSlug: null,
            search: location.search,
            template: null,
            type: 'notFoundPage',
            isFetching: false,
            status: 'idle',
        });
    };

    try {
        // @TODO refactor/move to utils?
        const { p: selectedPartNoParameter } = getQueryParams(location) || {};
        const selectedPartNoState = location.state?.presetPartNo;
        const parameters = {};
        if (selectedPartNoState || selectedPartNoParameter) {
            parameters.selected_variant_part_no = selectedPartNoState || selectedPartNoParameter;
        }

        // @TODO error handling?
        // if (!applicationId) {
        //     return rejectWithValue({ error: 'Unknown error' });
        // }

        const slugResponse = await ResolveSlug(applicationId, slug, parameters);
        const httpStatus = typeof slugResponse?.status === 'number' ? slugResponse.status : undefined;
        // Prefer wrapped `{ status, data }` from ResolveSlug; fall back to a raw JSON body.
        const response = slugResponse?.data !== undefined ? slugResponse.data : slugResponse;
        const type = response && typeof response === 'object' ? response.type : undefined;

        // Unresolved slug: no known page type. Includes JSON 404 and QA's current 500 HTML.
        if (!RESOLVED_SLUG_TYPES.has(type)) {
            return fulfillNotFoundPage();
        }

        if (httpStatus >= 500) {
            return rejectWithValue({ error: 'Unknown error' });
        }

        const {
            alternate,
            data: pageData,
            product_data: productData,
            relationships,
            template,
            slug: resolvedSlugFromApi,
        } = response;

        if (type === 'redirect') {
            // @TODO: check if this works for both SSR and Client
            let redirectSlug = response.data.to;
            if (location.search.charAt(0) === '?') {
                redirectSlug += location.search;
            }

            return fulfillWithValue({
                type,
                data: {
                    to: redirectSlug,
                    status: response.data?.status ?? httpStatus,
                },
                resolvedSlug: null,
                status: 'idle',
            });
        }

        if (type === 'product' && productData) {
            // @TODO Maybe use formatting/data fetching helpers
            // fetchProductData(action.payload?.data);

            const partNos = JSON.parse(JSON.stringify(productData.variants))?.map(
                (variant) => variant.partNo || variant.part_no,
            );

            if (productData?.partNo || productData?.part_no) partNos.push(productData.partNo || productData.part_no);
            const storeAvailabilityResponse = await GetStoreAvailabilityByPartNos(partNos, applicationId);

            productData.storeAvailability = storeAvailabilityResponse;

            // Todo: fix Datalayer product structure as it currently is not correctly handling variants
            if (!inServer) {
                try {
                    Events.trigger(ProductEvents.VIEW, {
                        data: productData,
                    });
                } catch (e) {
                    console.error('Error triggering ProductEvents.VIEW', e);
                }
            }
            pageData.product = productData;
        }

        const alternates = formatLocaleAlternates(alternate || [], pageData.localizations);
        return fulfillWithValue({
            data: pageData,
            applicationId: applicationId || null,
            localeAlternates: alternates,
            relationships,
            resolvedSlug: resolvedSlugFromApi ?? null,
            search: location.search,
            template,
            type,
            isFetching: false, // Todo, maybe remove this?
            status: 'idle',
        });
    } catch (e) {
        console.error('Error resolving page', e);
        return fulfillNotFoundPage();
    }
});

const pending = (state) => {
    state.isFetching = true; // Todo, maybe remove this?
    state.status = 'pending';
};

const fulfilled = (state, action) => {
    const camelizedData = objectKeysToCamelCase(action.payload, {
        recursive: true,
        modifyInputObject: false,
    });

    if (camelizedData.data != null && action.payload?.data?._references != null) {
        camelizedData.data.references = camelCaseMapValues(action.payload.data._references);
    }

    // Todo: check for duplicate history entries in GTM
    if (camelizedData.type !== 'product') {
        const { template, type, applicationId } = camelizedData;
        const { title } = camelizedData.data ?? {};
        try {
            Events.trigger(PageEvents.VIEW, {
                title,
                template,
                type,
                applicationId,
            });
        } catch (e) {
            console.error('Error triggering PageEvents.VIEW', e);
        }
    }

    return {
        ...state,
        ...camelizedData,
    };
};

const rejected = (state) => {
    state.isFetching = false; // Todo, maybe remove this?
    state.status = 'idle';
};

export const resolvePageStateCallbacks = {
    pending,
    fulfilled,
    rejected,
};
